import { useState, useRef, useEffect } from "react";
import { Microphone, X, PaperPlaneTilt, SpeakerHigh } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceFAB({ open, onToggle }) {
  const [listening, setListening] = useState(false);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      setResponse("Speech recognition not supported in this browser.");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  const sendQuery = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResponse("");
    try {
      const res = await axios.post(`${API}/ai-query`, { query, trade: "general" });
      setResponse(res.data.response);
    } catch {
      setResponse("Failed to get AI response. Please try again.");
    }
    setLoading(false);
  };

  const speakResponse = () => {
    if (!response || speaking) return;
    const utterance = new SpeechSynthesisUtterance(response);
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    return () => { window.speechSynthesis.cancel(); };
  }, []);

  return (
    <>
      <button
        data-testid="voice-command-button"
        onClick={onToggle}
        className={`voice-fab ${listening ? "listening" : ""}`}
      >
        {open ? <X size={24} weight="bold" /> : <Microphone size={24} weight="bold" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-[10rem] right-4 left-4 sm:left-auto sm:w-[360px] z-[10003] glass-overlay rounded-md p-4"
            data-testid="voice-panel"
          >
            <h3 className="text-white font-bold text-sm mb-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
              VOICE ASSISTANT
            </h3>

            <div className="flex gap-2 mb-3">
              <input
                data-testid="voice-query-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendQuery()}
                placeholder="Ask about codes, materials..."
                className="flex-1 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm border border-white/15 rounded-sm focus:outline-none focus:border-[#00E5FF]"
              />
              <button
                data-testid="voice-listen-btn"
                onClick={listening ? stopListening : startListening}
                className={`touch-target w-10 h-10 flex items-center justify-center rounded-sm transition-colors ${
                  listening ? "bg-[#FF5A00] text-white" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                <Microphone size={18} weight={listening ? "fill" : "regular"} />
              </button>
              <button
                data-testid="voice-send-btn"
                onClick={sendQuery}
                disabled={loading || !query.trim()}
                className="touch-target w-10 h-10 flex items-center justify-center bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] disabled:opacity-40 transition-colors"
              >
                <PaperPlaneTilt size={18} weight="bold" />
              </button>
            </div>

            {loading && (
              <div className="text-[#00E5FF] text-xs font-mono-data animate-pulse">
                PROCESSING...
              </div>
            )}

            {response && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#00E5FF] text-[10px] font-mono-data">AI RESPONSE</span>
                  <button
                    data-testid="voice-speak-btn"
                    onClick={speakResponse}
                    className={`p-1 rounded-sm transition-colors ${
                      speaking ? "text-[#FF5A00]" : "text-white/60 hover:text-white"
                    }`}
                  >
                    <SpeakerHigh size={14} weight={speaking ? "fill" : "regular"} />
                  </button>
                </div>
                <p className="text-white/90 text-xs leading-relaxed max-h-40 overflow-y-auto">
                  {response}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
